/**
 * Organisation tab: view-first + sectioned edit modal.
 *
 * Edit modal sections:
 *   1. Basic Information  — name, legal name, reg#, industry, website, founded, HQ, about
 *   2. Strategy          — Vision, Mission, Core Values (numbered list)
 *   3. Target Customer Segments
 *   4. Major Products or Services
 *   5. Quality Policy
 */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Modal from '../../components/Modal';
import LogoUploader from '../../components/workspace/LogoUploader';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatDate } from '../../lib/format';

// ─── helpers ────────────────────────────────────────────────────────────────

let _cv = -1;
const nextCvKey = () => _cv--;

const emptyValue = () => ({
  _key: nextCvKey(),
  value: '',
  description: '',
  accepted_behavior: '',
  unaccepted_behavior: '',
});

function parseValues(raw) {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.map((v) => ({ ...emptyValue(), ...v, _key: nextCvKey() }));
  } catch { return []; }
}

function serializeValues(arr) {
  return JSON.stringify(
    arr.map(({ _key, ...rest }) => rest), // eslint-disable-line no-unused-vars
  );
}

function initState(org) {
  return {
    name:                       org?.name                       ?? '',
    legal_name:                 org?.legal_name                 ?? '',
    registration_no:            org?.registration_no            ?? '',
    industry:                   org?.industry                   ?? '',
    website:                    org?.website                    ?? '',
    founded_on:                 org?.founded_on                 ?? '',
    hq_address:                 org?.hq_address                 ?? '',
    description:                org?.description                ?? '',
    vision:                     org?.vision                     ?? '',
    mission:                    org?.mission                    ?? '',
    coreValues:                 parseValues(org?.core_values),
    target_customer_segments:   org?.target_customer_segments   ?? '',
    major_products_services:    org?.major_products_services    ?? '',
    quality_policy:             org?.quality_policy             ?? '',
  };
}

// ─── view ────────────────────────────────────────────────────────────────────

export default function Organisation() {
  const { organisation, refreshOrganisation, canWrite } = useWorkspace();
  const [editOpen, setEditOpen] = useState(false);

  if (!organisation) return <p className="muted">Loading organisation…</p>;

  const coreValues = parseValues(organisation.core_values);

  return (
    <div className="org-view">

      {/* ── Identity card ─────────────────────────────────────────── */}
      <section className="org-identity-card">
        <div className="org-identity-left">
          <LogoUploader
            logoSrc={organisation.logo_data || null}
            canEdit={canWrite}
            onUploaded={refreshOrganisation}
            onRemoved={refreshOrganisation}
          />
        </div>
        <div className="org-identity-body">
          <div className="org-identity-header">
            <div>
              <h2 className="org-name">{organisation.name}</h2>
              {organisation.legal_name && (
                <p className="org-legal-name">{organisation.legal_name}</p>
              )}
            </div>
            {canWrite && (
              <button className="btn-primary" onClick={() => setEditOpen(true)}>
                Edit Details
              </button>
            )}
          </div>
          <div className="org-info-grid">
            {organisation.industry && (
              <OrgInfoRow label="Industry" value={organisation.industry} />
            )}
            {organisation.registration_no && (
              <OrgInfoRow label="Reg. No." value={organisation.registration_no} />
            )}
            {organisation.founded_on && (
              <OrgInfoRow label="Founded" value={formatDate(organisation.founded_on)} />
            )}
            {organisation.website && (
              <OrgInfoRow label="Website">
                <a href={organisation.website} target="_blank" rel="noopener noreferrer">
                  {organisation.website}
                </a>
              </OrgInfoRow>
            )}
            {organisation.hq_address && (
              <OrgInfoRow label="HQ Address" value={organisation.hq_address} full />
            )}
          </div>
          {organisation.description && (
            <p className="org-about-text">{organisation.description}</p>
          )}
        </div>
      </section>

      {/* ── Strategy card ─────────────────────────────────────────── */}
      {(organisation.vision || organisation.mission || coreValues.length > 0) && (
        <section className="org-section-card">
          <h3 className="org-section-title">Strategy</h3>
          <div className="org-strategy-grid">
            {organisation.vision && (
              <div className="org-strategy-block">
                <span className="org-strategy-label">Vision</span>
                <p className="org-strategy-text">{organisation.vision}</p>
              </div>
            )}
            {organisation.mission && (
              <div className="org-strategy-block">
                <span className="org-strategy-label">Mission</span>
                <p className="org-strategy-text">{organisation.mission}</p>
              </div>
            )}
          </div>
          {coreValues.length > 0 && (
            <div className="org-values-list">
              <span className="org-strategy-label" style={{ marginBottom: 10, display: 'block' }}>
                Core Values
              </span>
              <ol className="org-cv-ol">
                {coreValues.map((cv, i) => (
                  <li key={i} className="org-cv-item">
                    <strong className="org-cv-name">{cv.value}</strong>
                    {cv.description && (
                      <p className="muted" style={{ margin: '2px 0 6px', fontSize: 13 }}>{cv.description}</p>
                    )}
                    {(cv.accepted_behavior || cv.unaccepted_behavior) && (
                      <div className="cv-behaviour-tags">
                        {cv.accepted_behavior && cv.accepted_behavior.split(',').map((t) => t.trim()).filter(Boolean).map((t, j) => (
                          <span key={j} className="tag-chip">{t}</span>
                        ))}
                        {cv.unaccepted_behavior && cv.unaccepted_behavior.split(',').map((t) => t.trim()).filter(Boolean).map((t, j) => (
                          <span key={j} className="tag-chip tag-chip--danger">{t}</span>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </section>
      )}

      {/* ── Market & Operations cards ──────────────────────────────── */}
      {(organisation.target_customer_segments || organisation.major_products_services || organisation.quality_policy) && (
        <div className="org-extra-grid">
          {organisation.target_customer_segments && (
            <section className="org-section-card">
              <h3 className="org-section-title">Target Customer Segments</h3>
              <p className="org-section-text">{organisation.target_customer_segments}</p>
            </section>
          )}
          {organisation.major_products_services && (
            <section className="org-section-card">
              <h3 className="org-section-title">Major Products &amp; Services</h3>
              <p className="org-section-text">{organisation.major_products_services}</p>
            </section>
          )}
          {organisation.quality_policy && (
            <section className="org-section-card org-extra-grid--full">
              <h3 className="org-section-title">Quality Policy</h3>
              <p className="org-section-text">{organisation.quality_policy}</p>
            </section>
          )}
        </div>
      )}

      {!canWrite && (
        <p className="muted" style={{ marginTop: 12 }}>
          You have read-only access. Ask an admin to make changes.
        </p>
      )}

      <EditOrgModal
        open={editOpen}
        organisation={organisation}
        onClose={() => setEditOpen(false)}
        onSaved={refreshOrganisation}
      />
    </div>
  );
}

function OrgInfoRow({ label, value, children, full }) {
  return (
    <div className={`org-info-row${full ? ' org-info-row--full' : ''}`}>
      <span className="org-info-label">{label}</span>
      <span className="org-info-value">{children || value}</span>
    </div>
  );
}

// ─── edit modal ──────────────────────────────────────────────────────────────

function EditOrgModal({ open, organisation, onClose, onSaved }) {
  const { appId } = useParams();
  const api = useApi();

  const [values, setValues] = useState(() => initState(organisation));
  const [saving, setSaving]  = useState(false);
  const [error,  setError]   = useState(null);

  // `organisation` is workspace context — updated by silent background refresh.
  // Use a ref so the reset only fires when the modal opens, not on every refresh.
  const orgRef = React.useRef(organisation);
  orgRef.current = organisation;

  React.useEffect(() => {
    if (open) { setValues(initState(orgRef.current)); setSaving(false); setError(null); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  // ── core values helpers ──────────────────────────────────────────────────
  const addCoreValue    = () => setValues((s) => ({ ...s, coreValues: [...s.coreValues, emptyValue()] }));
  const removeCoreValue = (i) => setValues((s) => ({ ...s, coreValues: s.coreValues.filter((_, idx) => idx !== i) }));
  const updateCoreValue = (i, patch) => setValues((s) => {
    const arr = s.coreValues.slice();
    arr[i] = { ...arr[i], ...patch };
    return { ...s, coreValues: arr };
  });

  const handleSave = async (e) => {
    e.preventDefault();
    if (!values.name.trim()) { setError('Organisation name is required'); return; }
    setError(null);
    setSaving(true);
    try {
      await api.updateOrganisation(appId, {
        name:                       values.name.trim(),
        legal_name:                 values.legal_name.trim()               || null,
        registration_no:            values.registration_no.trim()          || null,
        industry:                   values.industry.trim()                  || null,
        website:                    values.website.trim()                   || null,
        founded_on:                 values.founded_on                       || null,
        hq_address:                 values.hq_address.trim()               || null,
        description:                values.description.trim()              || null,
        vision:                     values.vision.trim()                    || null,
        mission:                    values.mission.trim()                   || null,
        core_values:                values.coreValues.length > 0
                                      ? serializeValues(values.coreValues)
                                      : null,
        target_customer_segments:   values.target_customer_segments.trim() || null,
        major_products_services:    values.major_products_services.trim()  || null,
        quality_policy:             values.quality_policy.trim()           || null,
      });
      await onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed');
      setSaving(false);
    }
  };

  return (
    <Modal open={open} title="Edit Organisation" onClose={saving ? undefined : onClose} size="lg">
      <form onSubmit={handleSave} className="org-edit-form">

        {/* ── 1. Basic Information ─────────────────────────────────────── */}
        <OrgSection title="Basic Information">
          <div className="org-form-grid">
            <OrgField label="Organisation name" required>
              <input className="form-input" maxLength={200} value={values.name}
                onChange={(e) => set('name', e.target.value)} />
            </OrgField>
            <OrgField label="Legal name">
              <input className="form-input" maxLength={200} value={values.legal_name}
                onChange={(e) => set('legal_name', e.target.value)} />
            </OrgField>
            <OrgField label="Registration number">
              <input className="form-input" maxLength={100} value={values.registration_no}
                onChange={(e) => set('registration_no', e.target.value)} />
            </OrgField>
            <OrgField label="Industry">
              <input className="form-input" maxLength={100} value={values.industry}
                onChange={(e) => set('industry', e.target.value)} />
            </OrgField>
            <OrgField label="Website">
              <input className="form-input" maxLength={500} placeholder="https://…" value={values.website}
                onChange={(e) => set('website', e.target.value)} />
            </OrgField>
            <OrgField label="Founded on">
              <input className="form-input" type="date" value={values.founded_on}
                onChange={(e) => set('founded_on', e.target.value)} />
            </OrgField>
          </div>
          <OrgField label="HQ address">
            <textarea className="form-input" rows={2} maxLength={500} value={values.hq_address}
              onChange={(e) => set('hq_address', e.target.value)} />
          </OrgField>
          <OrgField label="About">
            <textarea className="form-input" rows={3} maxLength={5000} value={values.description}
              onChange={(e) => set('description', e.target.value)} />
          </OrgField>
        </OrgSection>

        {/* ── 2. Strategy ──────────────────────────────────────────────── */}
        <OrgSection title="Strategy">
          <OrgField label="Vision">
            <input className="form-input" maxLength={2000}
              placeholder="Where we are going — the aspirational future state"
              value={values.vision}
              onChange={(e) => set('vision', e.target.value)} />
          </OrgField>
          <OrgField label="Mission">
            <input className="form-input" maxLength={2000}
              placeholder="Why we exist — the purpose that drives us"
              value={values.mission}
              onChange={(e) => set('mission', e.target.value)} />
          </OrgField>

          {/* Core Values */}
          <div className="org-subsection-head">
            <span className="org-subsection-title">Core Values</span>
            <button type="button" className="btn-link-sm" onClick={addCoreValue}>+ Add Value</button>
          </div>
          {values.coreValues.length === 0 && (
            <p className="muted small" style={{ marginBottom: 8 }}>No core values yet.</p>
          )}
          {values.coreValues.map((cv, idx) => (
            <div key={cv._key} className="core-value-row">
              <div className="core-value-num">{idx + 1}</div>
              <div className="core-value-fields">
                <input className="form-input" maxLength={200} placeholder="Value (e.g. Integrity)"
                  value={cv.value}
                  onChange={(e) => updateCoreValue(idx, { value: e.target.value })} />
                <textarea className="form-input" rows={3} maxLength={1000} placeholder="Description"
                  value={cv.description}
                  onChange={(e) => updateCoreValue(idx, { description: e.target.value })} />
                <TagInput
                  value={cv.accepted_behavior}
                  onChange={(v) => updateCoreValue(idx, { accepted_behavior: v })}
                  placeholder="Accepted Behaviour — type and press comma" />
                <TagInput
                  value={cv.unaccepted_behavior}
                  onChange={(v) => updateCoreValue(idx, { unaccepted_behavior: v })}
                  placeholder="Unaccepted Behaviour — type and press comma"
                  variant="danger" />
              </div>
              <button type="button" className="btn-link-sm danger"
                onClick={() => removeCoreValue(idx)} title="Remove">×</button>
            </div>
          ))}
        </OrgSection>

        {/* ── 3. Target Customer Segments ──────────────────────────────── */}
        <OrgSection title="Target Customer Segments">
          <OrgField label="">
            <textarea className="form-input" rows={3} maxLength={5000}
              placeholder="Describe the customer groups or market segments this organisation serves"
              value={values.target_customer_segments}
              onChange={(e) => set('target_customer_segments', e.target.value)} />
          </OrgField>
        </OrgSection>

        {/* ── 4. Major Products or Services ────────────────────────────── */}
        <OrgSection title="Major Products or Services">
          <OrgField label="">
            <textarea className="form-input" rows={3} maxLength={5000}
              placeholder="List the main products or services offered"
              value={values.major_products_services}
              onChange={(e) => set('major_products_services', e.target.value)} />
          </OrgField>
        </OrgSection>

        {/* ── 5. Quality Policy ────────────────────────────────────────── */}
        <OrgSection title="Quality Policy">
          <OrgField label="">
            <textarea className="form-input" rows={4} maxLength={2000}
              placeholder="State the organisation's commitment to quality"
              value={values.quality_policy}
              onChange={(e) => set('quality_policy', e.target.value)} />
          </OrgField>
        </OrgSection>

        {error && <div className="form-error">{error}</div>}

        <div className="form-actions" style={{ marginTop: 8 }}>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit"  className="btn-primary"   disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── small sub-components ────────────────────────────────────────────────────

function OrgSection({ title, children }) {
  return (
    <div className="org-form-section">
      <h3 className="org-form-section-title">{title}</h3>
      {children}
    </div>
  );
}

function OrgField({ label, required, children }) {
  return (
    <label className="form-label" style={{ marginBottom: 0 }}>
      {label && (
        <span>
          {label}
          {required && <span style={{ color: '#900' }}> *</span>}
        </span>
      )}
      {children}
    </label>
  );
}

// ─── tag input (comma-separated chips) ──────────────────────────────────────

function TagInput({ value, onChange, placeholder, variant }) {
  const tags = value ? value.split(',').map((t) => t.trim()).filter(Boolean) : [];
  const [draft, setDraft] = React.useState('');

  const commit = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const next = [...tags, trimmed];
    onChange(next.join(', '));
    setDraft('');
  };

  const removeTag = (idx) => {
    const next = tags.filter((_, i) => i !== idx);
    onChange(next.join(', '));
  };

  const handleChange = (e) => {
    const v = e.target.value;
    if (v.includes(',')) {
      const parts = v.split(',');
      // commit all full parts except the last (which stays in the input)
      parts.slice(0, -1).forEach((p) => commit(p));
      setDraft(parts[parts.length - 1]);
    } else {
      setDraft(v);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(draft); }
    if (e.key === 'Backspace' && !draft && tags.length > 0) removeTag(tags.length - 1);
  };

  return (
    <div className="tag-input-wrap">
      {tags.map((tag, i) => (
        <span key={i} className={`tag-chip${variant ? ` tag-chip--${variant}` : ''}`}>
          {tag}
          <button type="button" className="tag-chip-remove" onClick={() => removeTag(i)}>×</button>
        </span>
      ))}
      <input
        className="tag-inner-input"
        value={draft}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={tags.length === 0 ? placeholder : ''}
      />
    </div>
  );
}
